import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  InputAdornment,
  ListItemButton,
  Paper,
  styled,
} from "@mui/material";
import { Field, FieldProps } from "formik";
import { ReactElement, useState } from "react";

import {
  ListSubaccountUsersQuery,
  ListSubaccountUsersQueryVariables,
  UserSortBy,
} from "~frontend/__generated__/graphql";
import FormTextField from "~frontend/components/forms/FormTextField";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";
import { LIST_SUBACCOUNT_USERS } from "~frontend/graphql/users";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useDebounce from "~frontend/util/useDebounce";

import { InboxMemberOption } from "./types";

const AutocompletePaper = styled(Paper)({
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: palette.lightGray,
    "&.Mui-focused": {
      backgroundColor: palette.lightGray,
    },
  },
});

export function FormikInboxMembersField({
  name,
}: {
  name: string;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const listSubaccountUsers = useQuery<
    ListSubaccountUsersQuery,
    ListSubaccountUsersQueryVariables
  >(LIST_SUBACCOUNT_USERS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        sort: UserSortBy.FullNameAsc,
        filter: { nameOrEmailContains: debouncedSearch },
      },
      subaccountId: subaccount.id,
    },
    skip: debouncedSearch.length < 3,
  });

  return (
    <Field name={name}>
      {(fieldProps: FieldProps<InboxMemberOption[]>) => {
        return (
          <Autocomplete
            multiple
            value={fieldProps.field.value}
            onChange={(_event, value) =>
              fieldProps.form.setFieldValue(fieldProps.field.name, value)
            }
            disableCloseOnSelect
            onBlur={event => {
              setSearch("");
              fieldProps.field.onBlur(event);
            }}
            noOptionsText={
              debouncedSearch.length < 3
                ? "Enter at least 3 characters to search..."
                : "No matching users found"
            }
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            renderOption={(props, option, { selected }) => (
              <ListItemButton
                key={option.id}
                {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
              >
                <Checkbox checked={selected} icon={<CheckBoxUnchecked />} />
                {option.fullName}
              </ListItemButton>
            )}
            options={listSubaccountUsers.data?.listSubaccountUsers.users ?? []}
            filterOptions={options => options}
            renderTags={value =>
              value.map(option => option.fullName).join(", ")
            }
            onInputChange={(event, newSearch) => {
              // don't clear search when selecting options
              if (event?.type === "change" || event?.type === "blur") {
                setSearch(newSearch);
              }
            }}
            renderInput={params => (
              <FormTextField
                {...params}
                label="Inbox Members"
                error={fieldProps.meta.touched && !!fieldProps.meta.error}
                helperText={fieldProps.meta.touched && fieldProps.meta.error}
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment
                        position="start"
                        sx={{ padding: theme.spacing(1) }}
                      >
                        <Search />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {listSubaccountUsers.loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: {
                    border: `1px solid ${palette.midGray} !important`,
                    fontSize: "12px",
                  },
                }}
              />
            )}
            PaperComponent={AutocompletePaper}
          />
        );
      }}
    </Field>
  );
}
